import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { GridMaterial } from "@babylonjs/materials/grid/gridMaterial";
import type { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import type { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import type { ThemeId } from "../config/tracks";
import { NEON_ROAD, type RoadPalette, type Track } from "../track/Track";
import { buildShipReflections, type ReflectionSky } from "./shipReflections";

/** What the craft reflect on Neon Circuit: a night sky over the cyan grid glow. */
const NEON_REFLECTION: ReflectionSky = ["#0b1030", "#3aa6e6", "#05060c"];

/**
 * Scenery for a course: sky, fog, ground and low-poly Y2K props, chosen by the
 * track's theme. Static props are merged into a few meshes (one draw call per
 * material) so a phone can carry them; the few live things (dolphins, lightning,
 * blinking beacons) are updated each frame. Built per track, disposed on swap.
 */

type RGB = [number, number, number];
const c3 = (h: string) => Color3.FromHexString(h);

interface ThemeDef {
  /** Sky dome: zenith, horizon and below-horizon colours (null = the plain neon backdrop). */
  sky: [string, string, string] | null;
  fog: string;
  fogDensity: number;
  hemi: { intensity: number; ground: string; diffuse: string };
  dir: { intensity: number; diffuse: string; direction: RGB };
  road: RoadPalette;
}

const THEMES: Record<ThemeId, ThemeDef> = {
  neon: {
    sky: null,
    fog: "#050514",
    fogDensity: 0.001,
    hemi: { intensity: 0.6, ground: "#1a1a33", diffuse: "#ffffff" },
    dir: { intensity: 0.8, diffuse: "#ffffff", direction: [-0.4, -1, 0.6] },
    road: NEON_ROAD,
  },
  ocean: {
    sky: ["#1c63f0", "#bfe9ff", "#0a4fb8"],
    fog: "#b8e3fb",
    fogDensity: 0.00075,
    hemi: { intensity: 0.85, ground: "#2b6fbf", diffuse: "#ffffff" },
    dir: { intensity: 0.9, diffuse: "#fff6e0", direction: [-0.5, -1, 0.3] },
    road: { cell: "#2c5282", line: "#c8fbff", glow: new Color3(0.8, 0.92, 1.0), rail: new Color3(0.3, 0.95, 1.0) },
  },
  port: {
    sky: ["#34185e", "#ff9a58", "#2a1840"],
    fog: "#f08a62",
    fogDensity: 0.0008,
    hemi: { intensity: 0.7, ground: "#402040", diffuse: "#ffd0b0" },
    dir: { intensity: 0.85, diffuse: "#ffb070", direction: [0.7, -0.5, 0.4] },
    road: { cell: "#1e1624", line: "#ffb347", glow: new Color3(0.75, 0.6, 0.5), rail: new Color3(1.0, 0.5, 0.15) },
  },
  city: {
    sky: ["#04030d", "#3a1266", "#0a0616"],
    fog: "#1c0a34",
    fogDensity: 0.0009,
    hemi: { intensity: 0.55, ground: "#200a30", diffuse: "#d8c8ff" },
    dir: { intensity: 0.5, diffuse: "#b8a0ff", direction: [-0.3, -1, -0.5] },
    road: { cell: "#0c0a18", line: "#ff4fd8", glow: new Color3(0.65, 0.5, 0.7), rail: new Color3(1.0, 0.2, 0.8) },
  },
  casino: {
    sky: ["#10001c", "#ff2e9a", "#1a0226"],
    fog: "#3a0840",
    fogDensity: 0.00095,
    hemi: { intensity: 0.6, ground: "#300838", diffuse: "#ffd0f0" },
    dir: { intensity: 0.6, diffuse: "#ffc0e8", direction: [0.4, -1, 0.3] },
    road: { cell: "#1c0822", line: "#ffd23f", glow: new Color3(0.75, 0.6, 0.45), rail: new Color3(1.0, 0.8, 0.2) },
  },
  desert: {
    sky: ["#3a86ff", "#ffe0a8", "#d08a48"],
    fog: "#f2cc92",
    fogDensity: 0.0007,
    hemi: { intensity: 0.9, ground: "#a86a38", diffuse: "#fff4e0" },
    dir: { intensity: 1.0, diffuse: "#fff0d0", direction: [-0.6, -1, -0.2] },
    road: { cell: "#2a1a10", line: "#ffcf7a", glow: new Color3(0.7, 0.6, 0.5), rail: new Color3(1.0, 0.65, 0.2) },
  },
  storm: {
    sky: ["#04060c", "#1d2848", "#080a12"],
    fog: "#121a2c",
    fogDensity: 0.0011,
    hemi: { intensity: 0.45, ground: "#101828", diffuse: "#b0c0ff" },
    dir: { intensity: 0.45, diffuse: "#a0b0ff", direction: [0.3, -1, 0.5] },
    road: { cell: "#0a0c16", line: "#9fb4ff", glow: new Color3(0.55, 0.6, 0.75), rail: new Color3(0.55, 0.7, 1.0) },
  },
  sky: {
    sky: ["#3f9bff", "#ffffff", "#f4f9ff"],
    fog: "#e6f4ff",
    fogDensity: 0.0006,
    hemi: { intensity: 1.0, ground: "#c8dcf0", diffuse: "#ffffff" },
    dir: { intensity: 0.8, diffuse: "#fffbe8", direction: [-0.4, -1, 0.5] },
    road: { cell: "#46587e", line: "#ffffff", glow: new Color3(0.8, 0.86, 0.95), rail: new Color3(0.9, 0.95, 1.0) },
  },
  space: {
    sky: ["#000004", "#12062e", "#000004"],
    fog: "#06030f",
    fogDensity: 0.0004,
    hemi: { intensity: 0.55, ground: "#100828", diffuse: "#d0c8ff" },
    dir: { intensity: 0.9, diffuse: "#e8e0ff", direction: [-0.6, -0.8, 0.4] },
    road: { cell: "#0a0618", line: "#b67cff", glow: new Color3(0.6, 0.5, 0.75), rail: new Color3(0.7, 0.4, 1.0) },
  },
};

/** Small deterministic PRNG so a course's scenery is the same every time. */
function rng(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return ((h ^= h >>> 16) >>> 0) / 4294967296;
  };
}

/** Paint every vertex of a mesh one colour (props get their colour this way,
 * so many of them can be merged under one material). */
function tint(m: Mesh, c: Color3): Mesh {
  const n = m.getTotalVertices();
  const cols = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    cols[i * 4] = c.r;
    cols[i * 4 + 1] = c.g;
    cols[i * 4 + 2] = c.b;
    cols[i * 4 + 3] = 1;
  }
  m.setVerticesData(VertexBuffer.ColorKind, cols);
  return m;
}

interface Dolphin {
  mesh: Mesh;
  state: "idle" | "jump";
  wait: number;
  t: number;
  dur: number;
  from: Vector3;
  to: Vector3;
  peak: number;
}

export class Environment {
  private readonly meshes: Mesh[] = [];
  private readonly mats: (StandardMaterial | GridMaterial)[] = [];
  private readonly textures: DynamicTexture[] = [];
  private readonly theme: ThemeId;
  private readonly def: ThemeDef;
  private readonly rand: () => number;

  // bounds of the course in plan, and its height range
  private minX = Infinity;
  private maxX = -Infinity;
  private minZ = Infinity;
  private maxZ = -Infinity;
  private roadMinY = Infinity;
  private roadMaxY = -Infinity;

  private propMat: StandardMaterial | null = null;
  private unlitMat: StandardMaterial | null = null;
  private domeMat: StandardMaterial | null = null;
  private waterTex: DynamicTexture | null = null;
  private dolphins: Dolphin[] = [];
  private waterY = 0;
  private time = 0;
  private flash = 0;
  private flashWait = 3;
  private bolt: Mesh | null = null;
  private beacons: Mesh | null = null;
  private readonly baseHemi: number;
  /** Tears down the craft's reflection map (built from this course's sky). */
  private readonly disposeReflections: () => void;

  constructor(
    private readonly scene: Scene,
    private readonly track: Track,
    private readonly hemi: HemisphericLight,
    private readonly dir: DirectionalLight
  ) {
    this.theme = track.spec.theme ?? "neon";
    this.def = THEMES[this.theme];
    this.rand = rng(track.spec.id);
    this.measure();
    this.applyAtmosphere();
    this.baseHemi = this.hemi.intensity;
    track.setPalette(this.def.road);
    if (this.def.sky) this.buildDome(this.def.sky);
    // The craft reflect this course's sky; Neon Circuit has none, so a night sky.
    this.disposeReflections = buildShipReflections(this.scene, this.def.sky ?? NEON_REFLECTION);
    switch (this.theme) {
      case "neon": this.buildNeon(); break;
      case "ocean": this.buildOcean(); break;
      case "port": this.buildPort(); break;
      case "city": this.buildCity(); break;
      case "casino": this.buildCasino(); break;
      case "desert": this.buildDesert(); break;
      case "storm": this.buildStorm(); break;
      case "sky": this.buildSky(); break;
      case "space": this.buildSpace(); break;
    }
    for (const m of this.meshes) if (!this.dolphins.some((d) => d.mesh === m) && m !== this.bolt) m.freezeWorldMatrix();
  }

  // ---- setup ----------------------------------------------------------------

  private measure(): void {
    for (const p of this.track.centerlineXZ()) {
      this.minX = Math.min(this.minX, p.x);
      this.maxX = Math.max(this.maxX, p.x);
      this.minZ = Math.min(this.minZ, p.z);
      this.maxZ = Math.max(this.maxZ, p.z);
    }
    for (let i = 0; i < 400; i++) {
      const y = this.track.pointAt(i / 400).y;
      this.roadMinY = Math.min(this.roadMinY, y);
      this.roadMaxY = Math.max(this.roadMaxY, y);
    }
  }

  private applyAtmosphere(): void {
    const d = this.def;
    this.scene.fogMode = Scene.FOGMODE_EXP2;
    this.scene.fogDensity = d.fogDensity;
    this.hemi.intensity = d.hemi.intensity;
    this.hemi.diffuse = c3(d.hemi.diffuse);
    this.dir.intensity = d.dir.intensity;
    this.dir.diffuse = c3(d.dir.diffuse);
    this.dir.direction = new Vector3(...d.dir.direction);
    if (!d.sky) {
      // Neon: exactly the original backdrop.
      this.scene.fogColor = new Color3(0.02, 0.02, 0.08);
      this.scene.clearColor = new Color4(0.02, 0.02, 0.06, 1);
      this.hemi.groundColor = new Color3(0.1, 0.1, 0.2);
      return;
    }
    this.scene.fogColor = c3(d.fog);
    const hz = c3(d.sky[1]);
    this.scene.clearColor = new Color4(hz.r, hz.g, hz.b, 1);
    this.hemi.groundColor = c3(d.hemi.ground);
  }

  private material(name: string, unlit: boolean): StandardMaterial {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = new Color3(1, 1, 1);
    m.specularColor = new Color3(0.08, 0.08, 0.08);
    if (unlit) {
      // Self-lit: the emissive white is tinted by each vertex colour, so every
      // prop merged under this material glows in its own colour.
      m.disableLighting = true;
      m.emissiveColor = new Color3(1, 1, 1);
      m.fogEnabled = false; // neon cuts through the haze
    } else {
      m.emissiveColor = new Color3(0.1, 0.1, 0.12);
    }
    this.mats.push(m);
    return m;
  }

  /** Lit, vertex-coloured material for merged props. */
  private props(): StandardMaterial {
    return (this.propMat ??= this.material("envProps", false));
  }
  /** Self-lit, vertex-coloured material for neon, windows and lamps. */
  private glow(): StandardMaterial {
    return (this.unlitMat ??= this.material("envGlow", true));
  }

  /** Merge parts into one static mesh under a material. */
  private bake(name: string, parts: Mesh[], mat: StandardMaterial, flatShade = true): Mesh | null {
    if (!parts.length) return null;
    if (flatShade) for (const p of parts) p.convertToFlatShadedMesh();
    const m = Mesh.MergeMeshes(parts, true, true);
    if (!m) return null;
    m.name = name;
    m.material = mat;
    m.isPickable = false;
    this.meshes.push(m);
    return m;
  }

  /** Is (x, z) at least `margin` clear of the road edge? */
  private clear(x: number, z: number, margin: number): boolean {
    const s = this.track.locate(new Vector3(x, this.roadMinY, z));
    return Math.abs(s.lateral) > s.halfWidth + margin;
  }

  /** A random spot `near`..`far` outside the course's bounding box, clear of the road. */
  private scatter(near: number, far: number, margin: number, inside = false): Vector3 | null {
    for (let tries = 0; tries < 12; tries++) {
      let x: number;
      let z: number;
      if (inside) {
        x = this.minX - near + this.rand() * (this.maxX - this.minX + near * 2);
        z = this.minZ - near + this.rand() * (this.maxZ - this.minZ + near * 2);
      } else {
        const cx = (this.minX + this.maxX) / 2;
        const cz = (this.minZ + this.maxZ) / 2;
        const rx = (this.maxX - this.minX) / 2 + near + this.rand() * (far - near);
        const rz = (this.maxZ - this.minZ) / 2 + near + this.rand() * (far - near);
        const a = this.rand() * Math.PI * 2;
        x = cx + Math.cos(a) * rx;
        z = cz + Math.sin(a) * rz;
      }
      if (this.clear(x, z, margin)) return new Vector3(x, 0, z);
    }
    return null;
  }

  /** A spot beside the road at progress t, `off` beyond the edge on a random side. */
  private roadside(t: number, off: number): Vector3 | null {
    const p = this.track.pointAt(t);
    const q = this.track.pointAt(t + 0.002);
    const fx = q.x - p.x;
    const fz = q.z - p.z;
    const l = Math.hypot(fx, fz) || 1;
    const side = this.rand() < 0.5 ? -1 : 1;
    const hw = this.track.halfWidth;
    const x = p.x + (fz / l) * side * (hw + off);
    const z = p.z - (fx / l) * side * (hw + off);
    return this.clear(x, z, off * 0.6) ? new Vector3(x, p.y, z) : null;
  }

  // ---- sky dome ---------------------------------------------------------------

  private buildDome([top, horizon, below]: [string, string, string]): void {
    const W = 1024;
    const H = 512;
    const tex = new DynamicTexture("envSky", { width: W, height: H }, this.scene, false);
    this.textures.push(tex);
    const ctx = tex.getContext() as CanvasRenderingContext2D;
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, top);
    g.addColorStop(0.47, horizon);
    g.addColorStop(0.5, horizon);
    // Just under the horizon the dome is the haze itself, so distant fogged
    // sea or ground meets it seamlessly, even looking down from high up.
    g.addColorStop(0.53, this.def.fog);
    g.addColorStop(0.72, this.def.fog);
    g.addColorStop(0.86, below);
    g.addColorStop(1, below);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    this.paintSky(ctx, W, H);
    tex.update();

    const dome = MeshBuilder.CreateSphere("envDome", { diameter: 8000, segments: 24, sideOrientation: Mesh.BACKSIDE }, this.scene);
    dome.infiniteDistance = true;
    dome.isPickable = false;
    const mat = new StandardMaterial("envDomeMat", this.scene);
    mat.disableLighting = true;
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.specularColor = new Color3(0, 0, 0);
    mat.emissiveTexture = tex;
    mat.fogEnabled = false;
    mat.backFaceCulling = false;
    dome.material = mat;
    this.domeMat = mat;
    this.mats.push(mat);
    this.meshes.push(dome);
  }

  /** Per-theme sky art: skylines, sun, stars, planet. Horizon is at H/2. */
  private paintSky(ctx: CanvasRenderingContext2D, W: number, H: number): void {
    const hz = H / 2;
    const skyline = (base: string, lit: string, maxH: number, count: number) => {
      for (let i = 0; i < count; i++) {
        const w = 6 + this.rand() * 22;
        const h = 8 + Math.pow(this.rand(), 1.6) * maxH;
        const x = this.rand() * W;
        ctx.fillStyle = base;
        ctx.fillRect(x, hz - h, w, h + 2);
        if (this.rand() < 0.3) ctx.fillRect(x + w / 2 - 1, hz - h - 10, 2, 10); // spire
        ctx.fillStyle = lit;
        for (let y = hz - h + 3; y < hz - 2; y += 4)
          for (let xx = x + 2; xx < x + w - 2; xx += 3) if (this.rand() < 0.25) ctx.fillRect(xx, y, 1, 1);
      }
    };
    const sun = (x: number, y: number, r: number, core: string, halo: string) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3);
      g.addColorStop(0, core);
      g.addColorStop(0.3, core);
      g.addColorStop(0.34, halo);
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r * 3, 0, Math.PI * 2);
      ctx.fill();
    };
    const stars = (n: number, maxY: number) => {
      for (let i = 0; i < n; i++) {
        const b = 0.4 + this.rand() * 0.6;
        ctx.fillStyle = `rgba(255,255,255,${b})`;
        const s = this.rand() < 0.08 ? 2 : 1;
        ctx.fillRect(this.rand() * W, this.rand() * maxY, s, s);
      }
    };
    switch (this.theme) {
      case "ocean":
        sun(W * 0.3, hz - 60, 18, "#fffef0", "rgba(255,250,220,0.35)");
        skyline("rgba(230,245,255,0.8)", "rgba(255,255,255,0.9)", 70, 18); // distant white resort towers
        break;
      case "port":
        sun(W * 0.62, hz - 22, 34, "#fff0b0", "rgba(255,160,90,0.45)");
        skyline("#2a1640", "#ffcf80", 150, 90); // super-tall skyline against the sunset
        break;
      case "city":
        stars(160, hz * 0.8);
        skyline("#0d0620", "#ff7ae0", 190, 140);
        skyline("#160a30", "#7ae8ff", 120, 80);
        break;
      case "casino":
        stars(120, hz * 0.7);
        skyline("#1c0428", "#ffd23f", 110, 70);
        break;
      case "desert":
        sun(W * 0.45, hz - 120, 26, "#fffbe8", "rgba(255,230,170,0.4)");
        for (let i = 0; i < 12; i++) {
          // mesas on the horizon
          const x = this.rand() * W;
          const w = 40 + this.rand() * 90;
          const h = 12 + this.rand() * 34;
          ctx.fillStyle = "rgba(170,90,50,0.75)";
          ctx.fillRect(x, hz - h, w, h + 3);
        }
        break;
      case "storm":
        stars(40, hz * 0.6);
        skyline("#06080f", "#ff3030", 130, 60);
        break;
      case "sky":
        for (let i = 0; i < 40; i++) {
          // cloud puffs around the horizon
          const x = this.rand() * W;
          const y = hz - 10 + this.rand() * 40;
          const r = 10 + this.rand() * 30;
          ctx.fillStyle = "rgba(255,255,255,0.85)";
          ctx.beginPath();
          ctx.ellipse(x, y, r * 2, r * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        sun(W * 0.2, hz - 150, 18, "#ffffff", "rgba(255,255,240,0.5)");
        break;
      case "space": {
        stars(900, H);
        // nebula haze
        for (let i = 0; i < 6; i++) {
          const x = this.rand() * W;
          const y = this.rand() * H;
          const g = ctx.createRadialGradient(x, y, 0, x, y, 120);
          g.addColorStop(0, i % 2 ? "rgba(180,60,255,0.18)" : "rgba(40,160,255,0.16)");
          g.addColorStop(1, "rgba(0,0,0,0)");
          ctx.fillStyle = g;
          ctx.fillRect(x - 120, y - 120, 240, 240);
        }
        // ringed planet
        const px = W * 0.7;
        const py = hz - 70;
        const pg = ctx.createLinearGradient(px - 60, py - 60, px + 60, py + 60);
        pg.addColorStop(0, "#ffb86b");
        pg.addColorStop(1, "#6a2c8a");
        ctx.fillStyle = pg;
        ctx.beginPath();
        ctx.arc(px, py, 56, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,220,180,0.7)";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.ellipse(px, py, 110, 22, -0.3, 0, Math.PI * 2);
        ctx.stroke();
        break;
      }
    }
  }

  // ---- shared props -------------------------------------------------------------

  /** The centre line sampled about every `step` units: position, and the
   * progress (0..1) each sample sits at. */
  private samples(step: number): { p: Vector3; t: number }[] {
    const n = Math.max(8, Math.round(this.track.length / step));
    const out: { p: Vector3; t: number }[] = [];
    for (let j = 0; j < n; j++) out.push({ p: this.track.pointAt(j / n), t: j / n });
    return out;
  }

  /** Pillars from the road down to `groundY` wherever it runs high above it —
   * but never down through another pass of the road (a crossing, a spiral). */
  private pillars(groundY: number, color: Color3, every = 260, minGap = 10): void {
    const parts: Mesh[] = [];
    const n = Math.round(this.track.length / every);
    const road = this.samples(30);
    const clearance = this.track.halfWidth + 14;
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const p = this.track.pointAt(t);
      // Under a cylinder the pillar holds up the tube's underside.
      const top = p.y - this.track.reachBelow(t);
      const h = top - 1.5 - groundY;
      if (h < minGap) continue;
      // Only under road that faces up: not off a wall, a loop or a corkscrew.
      if (this.track.frameAt(t).up.y < 0.7) continue;
      const pierces = road.some((s) => {
        let dt = Math.abs(s.t - t);
        dt = Math.min(dt, 1 - dt);
        return dt > 0.02 && s.p.y < top - 3 && Math.hypot(s.p.x - p.x, s.p.z - p.z) < clearance;
      });
      if (pierces) continue;
      const m = MeshBuilder.CreateCylinder("pillar", { height: h, diameterTop: 8, diameterBottom: 12, tessellation: 6 }, this.scene);
      m.position.set(p.x, groundY + h / 2, p.z);
      parts.push(tint(m, color));
    }
    this.bake("envPillars", parts, this.props());
  }

  /** Towers: a merged set of low-poly high-rises with lit window bands. */
  private towers(count: number, near: number, far: number, minH: number, maxH: number, palette: string[], lit: string, baseY: number, roadside = 0): void {
    const bodies: Mesh[] = [];
    const bands: Mesh[] = [];
    const litC = c3(lit);
    // The skyline always towers over the course, however high the road climbs.
    const top = this.roadMaxY - baseY;
    minH = Math.max(minH, top * 0.6);
    maxH = Math.max(maxH, top * 2.2);
    for (let i = 0; i < count; i++) {
      const at = roadside ? this.roadside(this.rand(), roadside + this.rand() * 220) : this.scatter(near, far, 90);
      if (!at) continue;
      const w = 40 + this.rand() * 90;
      const d = 40 + this.rand() * 90;
      const h = minH + Math.pow(this.rand(), 1.4) * (maxH - minH);
      const col = c3(palette[Math.floor(this.rand() * palette.length)]);
      const round = this.rand() < 0.3;
      const body = round
        ? MeshBuilder.CreateCylinder("tw", { height: h, diameter: w, tessellation: 8 }, this.scene)
        : MeshBuilder.CreateBox("tw", { width: w, height: h, depth: d }, this.scene);
      body.position.set(at.x, baseY + h / 2, at.z);
      body.rotation.y = this.rand() * Math.PI;
      bodies.push(tint(body, col));
      // stepped crown
      if (this.rand() < 0.5) {
        const cap = MeshBuilder.CreateBox("tc", { width: w * 0.6, height: h * 0.08, depth: d * 0.6 }, this.scene);
        cap.position.set(at.x, baseY + h + h * 0.04, at.z);
        cap.rotation.y = body.rotation.y;
        bodies.push(tint(cap, col.scale(0.85)));
      }
      if (this.rand() < 0.45) {
        const spire = MeshBuilder.CreateCylinder("ts", { height: h * 0.25, diameterTop: 0, diameterBottom: 6, tessellation: 4 }, this.scene);
        spire.position.set(at.x, baseY + h + h * 0.12, at.z);
        bodies.push(tint(spire, col.scale(0.7)));
      }
      // window bands
      const floors = Math.min(10, Math.floor(h / 60));
      for (let f = 1; f <= floors; f++) {
        if (this.rand() < 0.35) continue;
        const band = round
          ? MeshBuilder.CreateCylinder("tb", { height: 3, diameter: w + 1, tessellation: 8 }, this.scene)
          : MeshBuilder.CreateBox("tb", { width: w + 1, height: 3, depth: d + 1 }, this.scene);
        band.position.set(at.x, baseY + (h * f) / (floors + 1), at.z);
        band.rotation.y = body.rotation.y;
        bands.push(tint(band, litC));
      }
    }
    this.bake("envTowers", bodies, this.props());
    this.bake("envTowerLights", bands, this.glow(), false);
  }

  private palm(at: Vector3, parts: Mesh[], h = 34): void {
    const trunk = MeshBuilder.CreateCylinder("pt", { height: h, diameterTop: 2, diameterBottom: 4, tessellation: 5 }, this.scene);
    trunk.position.set(at.x, at.y + h / 2, at.z);
    trunk.rotation.z = (this.rand() - 0.5) * 0.3;
    parts.push(tint(trunk, c3("#8a5a34")));
    for (let i = 0; i < 6; i++) {
      const leaf = MeshBuilder.CreateBox("pl", { width: 3, height: 0.6, depth: 18 }, this.scene);
      const a = (i / 6) * Math.PI * 2;
      leaf.position.set(at.x + Math.sin(a) * 8, at.y + h - 2, at.z + Math.cos(a) * 8);
      leaf.rotation.set(0.45, a, 0);
      parts.push(tint(leaf, c3(i % 2 ? "#2fb35a" : "#46d16e")));
    }
  }

  private water(y: number, deep: string, shine: string): void {
    this.waterY = y;
    const g = MeshBuilder.CreateGround("envWater", { width: 9000, height: 9000, subdivisions: 36, updatable: true }, this.scene);
    const pos = g.getVerticesData(VertexBuffer.PositionKind);
    if (pos) {
      for (let i = 1; i < pos.length; i += 3) pos[i] = (this.rand() - 0.5) * 3; // faceted swell
      g.updateVerticesData(VertexBuffer.PositionKind, pos);
    }
    g.convertToFlatShadedMesh();
    g.position.y = y;
    g.position.x = (this.minX + this.maxX) / 2;
    g.position.z = (this.minZ + this.maxZ) / 2;
    const tex = new DynamicTexture("envWaterTex", { width: 128, height: 128 }, this.scene, true);
    this.textures.push(tex);
    const ctx = tex.getContext() as CanvasRenderingContext2D;
    ctx.fillStyle = deep;
    ctx.fillRect(0, 0, 128, 128);
    ctx.strokeStyle = shine;
    ctx.lineWidth = 2;
    for (let i = 0; i < 7; i++) {
      ctx.beginPath();
      const y0 = this.rand() * 128;
      ctx.moveTo(0, y0);
      for (let x = 0; x <= 128; x += 16) ctx.lineTo(x, y0 + Math.sin(x / 20 + i) * 5);
      ctx.stroke();
    }
    tex.update();
    tex.uScale = 45;
    tex.vScale = 45;
    this.waterTex = tex;
    const m = new StandardMaterial("envWaterMat", this.scene);
    m.diffuseTexture = tex;
    m.specularColor = new Color3(0.6, 0.7, 0.8);
    m.specularPower = 48;
    m.emissiveColor = c3(deep).scale(0.35);
    this.mats.push(m);
    g.material = m;
    g.isPickable = false;
    this.meshes.push(g);
  }

  private flatGround(y: number, main: string, line: string, ratio = 40): void {
    const floor = MeshBuilder.CreateGround("envFloor", { width: 9000, height: 9000 }, this.scene);
    floor.position.set((this.minX + this.maxX) / 2, y, (this.minZ + this.maxZ) / 2);
    const grid = new GridMaterial("envGrid", this.scene);
    grid.gridRatio = ratio;
    grid.majorUnitFrequency = 5;
    grid.mainColor = c3(main);
    grid.lineColor = c3(line);
    grid.opacity = 0.99;
    floor.material = grid;
    floor.isPickable = false;
    this.mats.push(grid);
    this.meshes.push(floor);
  }

  // ---- themes ---------------------------------------------------------------------

  /** The original backdrop: a vast faint neon grid far below. */
  private buildNeon(): void {
    const floor = MeshBuilder.CreateGround("floor", { width: 6000, height: 6000 }, this.scene);
    floor.position.y = -28;
    const grid = new GridMaterial("grid", this.scene);
    grid.gridRatio = 12;
    grid.majorUnitFrequency = 5;
    grid.mainColor = new Color3(0.02, 0.02, 0.08);
    grid.lineColor = new Color3(0.05, 0.12, 0.24);
    grid.opacity = 0.25;
    floor.material = grid;
    this.mats.push(grid);
    this.meshes.push(floor);
  }

  private buildOcean(): void {
    this.water(this.roadMinY - 16, "#0b5bd0", "#7fd8ff");
    this.pillars(this.waterY, c3("#e8f2ff"), 240, 6);
    // islands with palms
    const rock: Mesh[] = [];
    const palms: Mesh[] = [];
    for (let i = 0; i < 16; i++) {
      const at = i < 6 ? this.scatter(80, 500, 220, true) : this.scatter(150, 1200, 180);
      if (!at) continue;
      const r = 26 + this.rand() * 50;
      const isl = MeshBuilder.CreateCylinder("isl", { height: 14 + this.rand() * 18, diameterTop: r * 0.9, diameterBottom: r * 2, tessellation: 7 }, this.scene);
      isl.position.set(at.x, this.waterY + 6, at.z);
      rock.push(tint(isl, c3(this.rand() < 0.5 ? "#e8d9a8" : "#48b86a")));
      for (let k = 0; k < 2; k++) this.palm(new Vector3(at.x + (this.rand() - 0.5) * r * 0.6, this.waterY + 12, at.z + (this.rand() - 0.5) * r * 0.6), palms);
    }
    this.bake("envIslands", rock, this.props());
    this.bake("envPalms", palms, this.props());
    this.towers(10, 900, 1500, 180, 520, ["#f4f8ff", "#dff3ff", "#bfe6ff"], "#7fe8ff", this.waterY);
    this.makeDolphins(6);
  }

  private buildPort(): void {
    this.water(this.roadMinY - 14, "#20306a", "#ff9a6a");
    this.pillars(this.waterY, c3("#b8b0c0"), 220, 6);
    // container yards
    const boxes: Mesh[] = [];
    const cols = ["#e6392f", "#1f8fe8", "#f2c230", "#2fb36a", "#ff6a1f", "#e8e8e8"];
    for (let y = 0; y < 14; y++) {
      const at = this.roadside(this.rand(), 60 + this.rand() * 160);
      if (!at) continue;
      const yaw = this.rand() * Math.PI;
      const rows = 2 + Math.floor(this.rand() * 4);
      for (let r = 0; r < rows; r++)
        for (let k = 0; k < 4; k++)
          for (let s = 0; s < 1 + Math.floor(this.rand() * 3); s++) {
            const b = MeshBuilder.CreateBox("ct", { width: 8, height: 8, depth: 22 }, this.scene);
            const lx = (k - 1.5) * 9;
            const lz = (r - rows / 2) * 24;
            b.position.set(at.x + Math.cos(yaw) * lx + Math.sin(yaw) * lz, this.waterY + 10 + s * 8.2, at.z - Math.sin(yaw) * lx + Math.cos(yaw) * lz);
            b.rotation.y = yaw;
            boxes.push(tint(b, c3(cols[Math.floor(this.rand() * cols.length)])));
          }
      // quay slab under the stack
      const slab = MeshBuilder.CreateBox("qs", { width: 60, height: 6, depth: rows * 26 + 20 }, this.scene);
      slab.position.set(at.x, this.waterY + 3, at.z);
      slab.rotation.y = yaw;
      boxes.push(tint(slab, c3("#7a7488")));
    }
    // gantry cranes
    for (let i = 0; i < 6; i++) {
      const at = this.roadside(this.rand(), 90 + this.rand() * 120);
      if (!at) continue;
      const yaw = this.rand() * Math.PI;
      const yellow = c3(i % 2 ? "#ffcc2a" : "#ff5a2a");
      const legH = 70;
      for (const [lx, lz] of [[-14, -10], [14, -10], [-14, 10], [14, 10]]) {
        const leg = MeshBuilder.CreateBox("cl", { width: 3, height: legH, depth: 3 }, this.scene);
        leg.position.set(at.x + Math.cos(yaw) * lx + Math.sin(yaw) * lz, this.waterY + legH / 2, at.z - Math.sin(yaw) * lx + Math.cos(yaw) * lz);
        leg.rotation.y = yaw;
        boxes.push(tint(leg, yellow));
      }
      const boom = MeshBuilder.CreateBox("cb", { width: 4, height: 4, depth: 110 }, this.scene);
      boom.position.set(at.x, this.waterY + legH + 4, at.z);
      boom.rotation.y = yaw;
      boxes.push(tint(boom, yellow));
      const cab = MeshBuilder.CreateBox("cc", { width: 10, height: 8, depth: 10 }, this.scene);
      cab.position.set(at.x, this.waterY + legH - 4, at.z);
      boxes.push(tint(cab, c3("#f0f0f0")));
    }
    // lighthouse
    const lh = this.scatter(60, 300, 120);
    if (lh) {
      for (let s = 0; s < 6; s++) {
        const seg = MeshBuilder.CreateCylinder("lh", { height: 14, diameterTop: 14 - s, diameterBottom: 15 - s, tessellation: 8 }, this.scene);
        seg.position.set(lh.x, this.waterY + 7 + s * 14, lh.z);
        boxes.push(tint(seg, c3(s % 2 ? "#ffffff" : "#e6392f")));
      }
    }
    this.bake("envPort", boxes, this.props());
    this.towers(60, 350, 1300, 250, 1100, ["#3a2a5a", "#5a3a70", "#2a3a6a", "#6a4a80", "#8a5a7a"], "#ffcf80", this.waterY);
    this.blinkers(["#ff3030"], 30, this.waterY + 60);
  }

  private buildCity(): void {
    this.flatGround(this.roadMinY - 30, "#0a0616", "#3a1666");
    this.pillars(this.roadMinY - 30, c3("#2a2040"), 260, 14);
    this.towers(90, 0, 0, 180, 850, ["#1a1030", "#24163e", "#141a3a", "#2a1840"], "#ff7ae0", this.roadMinY - 30, 40);
    this.towers(40, 300, 1200, 400, 1300, ["#120c26", "#1c1234"], "#7ae8ff", this.roadMinY - 30);
    this.billboards(["#ff2e9a", "#00d7f2", "#d8f600", "#ff5a1f"], 18);
  }

  private buildCasino(): void {
    this.flatGround(this.roadMinY - 30, "#16061e", "#ffd23f", 30);
    this.pillars(this.roadMinY - 30, c3("#401848"), 200, 12);
    const parts: Mesh[] = [];
    // giant chips and dice scattered around the loops
    for (let i = 0; i < 26; i++) {
      const at = this.scatter(40, 700, 70, i < 14);
      if (!at) continue;
      if (this.rand() < 0.6) {
        const stack = 1 + Math.floor(this.rand() * 5);
        const col = c3(["#e6392f", "#1f8fe8", "#18181c", "#2fb36a", "#f2f2f2"][Math.floor(this.rand() * 5)]);
        for (let s = 0; s < stack; s++) {
          const chip = MeshBuilder.CreateCylinder("chip", { height: 6, diameter: 60, tessellation: 12 }, this.scene);
          chip.position.set(at.x + (this.rand() - 0.5) * 4, this.roadMinY - 27 + s * 6.2, at.z + (this.rand() - 0.5) * 4);
          parts.push(tint(chip, s % 2 ? col : c3("#f2f2f2")));
        }
      } else {
        const die = MeshBuilder.CreateBox("die", { size: 40 }, this.scene);
        die.position.set(at.x, this.roadMinY - 10, at.z);
        die.rotation.set(this.rand(), this.rand() * 3, this.rand());
        parts.push(tint(die, c3(this.rand() < 0.5 ? "#f4f4f4" : "#d0202a")));
      }
    }
    const palms: Mesh[] = [];
    for (let i = 0; i < 20; i++) {
      const at = this.roadside(this.rand(), 30 + this.rand() * 40);
      if (at) this.palm(new Vector3(at.x, this.roadMinY - 30, at.z), palms, 44);
    }
    this.bake("envCasino", parts, this.props());
    this.bake("envPalms", palms, this.props());
    this.arches(6, ["#ff2e9a", "#ffd23f", "#00d7f2"]);
    this.towers(30, 400, 1200, 250, 700, ["#3a0a4a", "#4a1a5a", "#2a0a3a"], "#ffd23f", this.roadMinY - 30);
    this.blinkers(["#ffd23f", "#ff2e9a"], 40, this.roadMinY + 20);
  }

  private buildDesert(): void {
    // The sand follows the road: it banks up into dunes under the high road and
    // is cut into a canyon round the low road (so where the dune road crosses
    // the canyon, it bridges it). Far off it settles to rolling open desert.
    const base = (this.roadMinY + this.roadMaxY) / 2 - 20;
    const g = MeshBuilder.CreateGround("envDunes", { width: 6400, height: 6400, subdivisions: 128, updatable: true }, this.scene);
    const cx = (this.minX + this.maxX) / 2;
    const cz = (this.minZ + this.maxZ) / 2;
    g.position.set(cx, 0, cz);
    const pos = g.getVerticesData(VertexBuffer.PositionKind);
    const cols: number[] = [];
    const hw = this.track.halfWidth;
    // Where the road banks, its low edge sits below the centre line: keep the
    // sand under that edge, not just under the middle.
    const road = this.samples(25).map((s) => ({ ...s, top: s.p.y - 12 - hw * Math.abs(Math.sin(this.track.locate(s.p).bank)) - this.track.reachBelow(s.t) }));
    let lo = Infinity;
    let hi = -Infinity;
    if (pos) {
      for (let i = 0; i < pos.length; i += 3) {
        const x = pos[i] + cx;
        const z = pos[i + 2] + cz;
        let h = base + 18 * Math.sin(x / 260) * Math.cos(z / 310) + 12 * Math.sin((x + z) / 170) + 26 * Math.max(0, Math.sin(x / 700 + z / 900));
        let bank = -Infinity; // highest embankment under any road
        let cut = Infinity; // lowest canyon cut round any road
        for (const s of road) {
          const off = Math.hypot(s.p.x - x, s.p.z - z) - hw;
          if (off > 600) continue;
          bank = Math.max(bank, s.top - Math.max(0, off) * 0.35);
          cut = Math.min(cut, s.top + Math.max(0, off - 20) * 1.2);
        }
        h = Math.min(Math.max(h, bank), cut);
        pos[i + 1] = h;
        lo = Math.min(lo, h);
        hi = Math.max(hi, h);
      }
      for (let i = 1; i < pos.length; i += 3) {
        const t = Math.max(0, Math.min(1, (pos[i] - lo) / Math.max(1, hi - lo)));
        cols.push(0.66 + 0.3 * t, 0.4 + 0.32 * t, 0.24 + 0.18 * t, 1);
      }
      g.updateVerticesData(VertexBuffer.PositionKind, pos);
      g.setVerticesData(VertexBuffer.ColorKind, cols);
    }
    g.convertToFlatShadedMesh();
    g.material = this.props();
    g.isPickable = false;
    this.meshes.push(g);
    // mesas, rock arches, pylons
    const parts: Mesh[] = [];
    for (let i = 0; i < 18; i++) {
      const at = this.scatter(200, 1400, 150);
      if (!at) continue;
      const h = 80 + this.rand() * 220;
      const mesa = MeshBuilder.CreateCylinder("mesa", { height: h, diameterTop: 90 + this.rand() * 120, diameterBottom: 180 + this.rand() * 160, tessellation: 6 }, this.scene);
      mesa.position.set(at.x, base + h / 2 - 10, at.z);
      parts.push(tint(mesa, c3(this.rand() < 0.5 ? "#c0643a" : "#a8522e")));
    }
    for (let i = 0; i < 5; i++) {
      const at = this.scatter(100, 600, 120, true);
      if (!at) continue;
      const r = 50 + this.rand() * 40;
      const path: Vector3[] = [];
      const yaw = this.rand() * Math.PI;
      for (let k = 0; k <= 8; k++) {
        const a = (k / 8) * Math.PI;
        path.push(new Vector3(at.x + Math.cos(a) * r * Math.cos(yaw), base + Math.sin(a) * r, at.z + Math.cos(a) * r * Math.sin(yaw)));
      }
      const arch = MeshBuilder.CreateTube("arch", { path, radius: 10, tessellation: 5, cap: Mesh.CAP_ALL }, this.scene);
      parts.push(tint(arch, c3("#d0784a")));
    }
    for (let i = 0; i < 10; i++) {
      const at = this.roadside(this.rand(), 70);
      if (!at) continue;
      const pylon = MeshBuilder.CreateCylinder("py", { height: 60, diameterTop: 2, diameterBottom: 7, tessellation: 4 }, this.scene);
      pylon.position.set(at.x, at.y + 20, at.z);
      parts.push(tint(pylon, c3("#e8e0d0")));
      const dish = MeshBuilder.CreateCylinder("dish", { height: 3, diameterTop: 22, diameterBottom: 6, tessellation: 8 }, this.scene);
      dish.position.set(at.x, at.y + 52, at.z);
      dish.rotation.x = 0.8;
      parts.push(tint(dish, c3("#f4f4f4")));
    }
    this.bake("envDesert", parts, this.props());
  }

  private buildStorm(): void {
    const groundY = this.roadMinY - 40;
    this.flatGround(groundY, "#070a12", "#1d2848");
    this.pillars(groundY, c3("#303a50"), 200, 12);
    const parts: Mesh[] = [];
    // the spire at the heart of the helix: where the loop turns about most tightly
    const pts = this.track.centerlineXZ();
    let sx = 0;
    let sz = 0;
    for (const p of pts) {
      sx += p.x;
      sz += p.z;
    }
    const spire = this.spireCentre() ?? new Vector3(sx / pts.length, 0, sz / pts.length);
    const tower = MeshBuilder.CreateCylinder("spire", { height: this.roadMaxY - groundY + 260, diameterTop: 60, diameterBottom: 220, tessellation: 8 }, this.scene);
    tower.position.set(spire.x, groundY + (this.roadMaxY - groundY + 260) / 2, spire.z);
    parts.push(tint(tower, c3("#1a2034")));
    this.bake("envStorm", parts, this.props());
    const rings: Mesh[] = [];
    for (let k = 1; k <= 8; k++) {
      const ring = MeshBuilder.CreateTorus("sr", { diameter: 200 - k * 16, thickness: 4, tessellation: 16 }, this.scene);
      ring.position.set(spire.x, groundY + k * ((this.roadMaxY - groundY + 240) / 8), spire.z);
      rings.push(tint(ring, c3(k % 2 ? "#6fa0ff" : "#b8d0ff")));
    }
    this.bake("envSpireRings", rings, this.glow(), false);
    this.towers(40, 300, 1300, 200, 700, ["#0c1020", "#121a2c", "#0a0e18"], "#6f8cff", groundY);
    this.blinkers(["#ff2020"], 36, groundY + 200);
    // a lightning bolt that flickers in the distance
    const path: Vector3[] = [];
    let x = 0;
    for (let k = 0; k <= 10; k++) {
      path.push(new Vector3(x, 900 - k * 90, 0));
      x += (this.rand() - 0.5) * 80;
    }
    const bolt = MeshBuilder.CreateTube("bolt", { path, radius: 3, tessellation: 4 }, this.scene);
    tint(bolt, c3("#e8f0ff"));
    bolt.material = this.glow();
    bolt.setEnabled(false);
    bolt.isPickable = false;
    this.bolt = bolt;
    this.meshes.push(bolt);
  }

  /** Centre of the tightest repeated loop in plan (a helix's axis), if any. */
  private spireCentre(): Vector3 | null {
    // A helix makes many samples share a small circle; take the centroid of the
    // densest 600u cell of the plan.
    const cells = new Map<string, { n: number; x: number; z: number }>();
    for (const p of this.track.centerlineXZ()) {
      const k = `${Math.floor(p.x / 600)},${Math.floor(p.z / 600)}`;
      const c = cells.get(k) ?? { n: 0, x: 0, z: 0 };
      c.n++;
      c.x += p.x;
      c.z += p.z;
      cells.set(k, c);
    }
    let best: { n: number; x: number; z: number } | null = null;
    for (const c of cells.values()) if (!best || c.n > best.n) best = c;
    return best && best.n > 60 ? new Vector3(best.x / best.n, 0, best.z / best.n) : null;
  }

  private buildSky(): void {
    // a sea of cloud far below, puffs drifting around the course, floating islands
    const cloudY = this.roadMinY - 180;
    const sea = MeshBuilder.CreateGround("envCloudSea", { width: 9000, height: 9000, subdivisions: 30, updatable: true }, this.scene);
    const pos = sea.getVerticesData(VertexBuffer.PositionKind);
    if (pos) {
      for (let i = 1; i < pos.length; i += 3) pos[i] = this.rand() * 18;
      sea.updateVerticesData(VertexBuffer.PositionKind, pos);
    }
    sea.position.set((this.minX + this.maxX) / 2, cloudY, (this.minZ + this.maxZ) / 2);
    tint(sea, c3("#ffffff"));
    sea.convertToFlatShadedMesh();
    sea.material = this.props();
    sea.isPickable = false;
    this.meshes.push(sea);
    this.pillars(cloudY, c3("#dfe8f4"), 300, 20);
    const puffs: Mesh[] = [];
    for (let i = 0; i < 36; i++) {
      const at = this.scatter(60, 1400, 120, i < 12);
      if (!at) continue;
      const y = cloudY + 40 + this.rand() * (this.roadMaxY - cloudY + 80);
      const col = c3(this.rand() < 0.25 ? "#ffe6f2" : "#ffffff");
      for (let k = 0; k < 4; k++) {
        const s = MeshBuilder.CreateSphere("cp", { segments: 3, diameterX: 90 + this.rand() * 80, diameterY: 40 + this.rand() * 30, diameterZ: 70 + this.rand() * 60 }, this.scene);
        s.position.set(at.x + (this.rand() - 0.5) * 90, y + this.rand() * 20, at.z + (this.rand() - 0.5) * 70);
        puffs.push(tint(s, col));
      }
    }
    this.bake("envClouds", puffs, this.props());
    const isles: Mesh[] = [];
    for (let i = 0; i < 12; i++) {
      const at = this.scatter(100, 1100, 160);
      if (!at) continue;
      const r = 60 + this.rand() * 90;
      const y = this.roadMinY - 60 + this.rand() * 200;
      const rock = MeshBuilder.CreateCylinder("fr", { height: r * 1.3, diameterTop: r * 2, diameterBottom: 0, tessellation: 6 }, this.scene);
      rock.position.set(at.x, y - r * 0.65, at.z);
      isles.push(tint(rock, c3("#8a7a6a")));
      const grass = MeshBuilder.CreateCylinder("fg", { height: 6, diameter: r * 2.05, tessellation: 6 }, this.scene);
      grass.position.set(at.x, y + 3, at.z);
      isles.push(tint(grass, c3("#5ad06e")));
    }
    this.bake("envIsles", isles, this.props());
    this.towers(18, 500, 1400, 300, 900, ["#f4f8ff", "#e0ecff", "#cfe0ff"], "#8fd0ff", cloudY);
  }

  private buildSpace(): void {
    const parts: Mesh[] = [];
    for (let i = 0; i < 90; i++) {
      const at = this.scatter(40, 1500, 90, i < 30);
      if (!at) continue;
      const r = 6 + Math.pow(this.rand(), 2.2) * 70;
      const rock = MeshBuilder.CreateIcoSphere("ast", { radius: r, subdivisions: 1 }, this.scene);
      rock.position.set(at.x, this.roadMinY - 120 + this.rand() * (this.roadMaxY - this.roadMinY + 300), at.z);
      rock.scaling.set(1, 0.6 + this.rand() * 0.6, 0.8 + this.rand() * 0.5);
      rock.rotation.set(this.rand() * 3, this.rand() * 3, this.rand() * 3);
      parts.push(tint(rock, c3(["#6a6070", "#7a6a5a", "#50506a", "#8a7a70"][Math.floor(this.rand() * 4)])));
    }
    this.bake("envAsteroids", parts, this.props());
    this.arches(7, ["#00d7f2", "#b67cff", "#ff2e9a"], 1.9);
  }

  // ---- neon extras --------------------------------------------------------------

  /** Glowing rings the road passes through. */
  private arches(count: number, cols: string[], scale = 1.5): void {
    const rings: Mesh[] = [];
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      const p = this.track.pointAt(t);
      const q = this.track.pointAt(t + 0.003);
      const yaw = Math.atan2(q.x - p.x, q.z - p.z);
      const r = this.track.halfWidth * scale;
      const ring = MeshBuilder.CreateTorus("arch", { diameter: r * 2, thickness: 3, tessellation: 24 }, this.scene);
      ring.rotation.set(Math.PI / 2, yaw, 0);
      ring.position.set(p.x, p.y + r * 0.35, p.z);
      rings.push(tint(ring, c3(cols[i % cols.length])));
    }
    this.bake("envArches", rings, this.glow(), false);
  }

  private billboards(cols: string[], count: number): void {
    const boards: Mesh[] = [];
    for (let i = 0; i < count; i++) {
      const at = this.roadside(this.rand(), 20 + this.rand() * 40);
      if (!at) continue;
      const w = 40 + this.rand() * 40;
      const b = MeshBuilder.CreateBox("bb", { width: w, height: w * 0.45, depth: 1 }, this.scene);
      const t = this.track.locate(at);
      b.position.set(at.x, at.y + 30 + this.rand() * 40, at.z);
      b.rotation.y = Math.atan2(t.forward.x, t.forward.z) + Math.PI / 2;
      boards.push(tint(b, c3(cols[i % cols.length])));
    }
    this.bake("envBillboards", boards, this.glow(), false);
  }

  /** Blinking aircraft-warning lights on the skyline. */
  private blinkers(cols: string[], count: number, y: number): void {
    const lamps: Mesh[] = [];
    for (let i = 0; i < count; i++) {
      const at = this.scatter(200, 1300, 80);
      if (!at) continue;
      const s = MeshBuilder.CreateSphere("bk", { diameter: 7, segments: 2 }, this.scene);
      s.position.set(at.x, y + this.rand() * 500, at.z);
      lamps.push(tint(s, c3(cols[i % cols.length])));
    }
    this.beacons = this.bake("envBeacons", lamps, this.glow(), false);
  }

  // ---- dolphins -------------------------------------------------------------------

  private makeDolphins(count: number): void {
    const body = MeshBuilder.CreateSphere("db", { segments: 3, diameterX: 2.6, diameterY: 2.4, diameterZ: 10 }, this.scene);
    const belly = MeshBuilder.CreateSphere("dbe", { segments: 3, diameterX: 2.0, diameterY: 1.4, diameterZ: 7 }, this.scene);
    belly.position.set(0, -0.6, 0.4);
    const snout = MeshBuilder.CreateCylinder("ds", { height: 2.6, diameterTop: 0.5, diameterBottom: 1.1, tessellation: 5 }, this.scene);
    snout.rotation.x = Math.PI / 2;
    snout.position.set(0, -0.2, 5.6);
    const fin = MeshBuilder.CreateCylinder("df", { height: 2.2, diameterTop: 0, diameterBottom: 2.4, tessellation: 3 }, this.scene);
    fin.scaling.x = 0.35;
    fin.rotation.x = -0.5;
    fin.position.set(0, 1.9, -0.6);
    const tail = MeshBuilder.CreateBox("dt", { width: 4.4, height: 0.3, depth: 1.6 }, this.scene);
    tail.position.set(0, 0, -5.4);
    const flipL = MeshBuilder.CreateBox("dl", { width: 2.2, height: 0.25, depth: 1.0 }, this.scene);
    flipL.position.set(1.4, -0.7, 1.6);
    flipL.rotation.set(0.2, -0.5, -0.4);
    const flipR = flipL.clone("dr");
    flipR.position.x = -1.4;
    flipR.rotation.set(0.2, 0.5, 0.4);
    const grey = c3("#5f86b8");
    for (const m of [body, snout, fin, tail, flipL, flipR]) tint(m, grey);
    tint(belly, c3("#dce8f6"));
    const proto = this.bake("envDolphin", [body, belly, snout, fin, tail, flipL, flipR], this.props());
    if (!proto) return;
    proto.scaling.setAll(1.4);
    proto.setEnabled(false);
    this.dolphins.push({ mesh: proto, state: "idle", wait: 0.5, t: 0, dur: 1, from: new Vector3(), to: new Vector3(), peak: 0 });
    for (let i = 1; i < count; i++) {
      const m = proto.clone(`envDolphin${i}`);
      if (!m) continue;
      m.setEnabled(false);
      this.meshes.push(m);
      this.dolphins.push({ mesh: m, state: "idle", wait: 0.3 + i * 0.7, t: 0, dur: 1, from: new Vector3(), to: new Vector3(), peak: 0 });
    }
  }

  private launchDolphin(d: Dolphin, focusT: number): void {
    const ahead = focusT + (180 + this.rand() * 520) / this.track.length;
    const at = this.roadside(ahead, 30 + this.rand() * 110);
    if (!at) {
      d.wait = 0.4;
      return;
    }
    const heading = this.rand() * Math.PI * 2;
    const len = 26 + this.rand() * 26;
    d.from.set(at.x, this.waterY, at.z);
    d.to.set(at.x + Math.sin(heading) * len, this.waterY, at.z + Math.cos(heading) * len);
    d.peak = 12 + this.rand() * 10;
    d.dur = 1.1 + this.rand() * 0.5;
    d.t = 0;
    d.state = "jump";
    d.mesh.setEnabled(true);
  }

  // ---- per frame ------------------------------------------------------------------

  /** `focus`: where the player is (for spawning dolphins in view); null in menus. */
  update(dt: number, focusT: number | null): void {
    this.time += dt;
    if (this.waterTex) {
      this.waterTex.uOffset = (this.waterTex.uOffset + dt * 0.012) % 1;
      this.waterTex.vOffset = (this.waterTex.vOffset + dt * 0.007) % 1;
    }
    for (const d of this.dolphins) {
      if (d.state === "idle") {
        d.wait -= dt;
        if (d.wait <= 0 && focusT !== null) this.launchDolphin(d, focusT);
        continue;
      }
      d.t += dt / d.dur;
      if (d.t >= 1) {
        d.state = "idle";
        d.mesh.setEnabled(false);
        d.wait = 0.6 + this.rand() * 2.6;
        continue;
      }
      const u = d.t;
      const x = d.from.x + (d.to.x - d.from.x) * u;
      const z = d.from.z + (d.to.z - d.from.z) * u;
      const y = this.waterY - 4 + (d.peak + 4) * 4 * u * (1 - u);
      d.mesh.position.set(x, y, z);
      const slope = ((d.peak + 4) * 4 * (1 - 2 * u)) / Math.hypot(d.to.x - d.from.x, d.to.z - d.from.z);
      d.mesh.rotation.set(-Math.atan(slope), Math.atan2(d.to.x - d.from.x, d.to.z - d.from.z), 0);
    }
    if (this.beacons) this.beacons.setEnabled(Math.floor(this.time * 1.4) % 2 === 0);
    if (this.theme === "storm") this.lightning(dt);
  }

  private lightning(dt: number): void {
    this.flashWait -= dt;
    if (this.flashWait <= 0) {
      this.flash = 0.35;
      this.flashWait = 2.5 + this.rand() * 6;
      if (this.bolt) {
        const a = this.rand() * Math.PI * 2;
        const cx = (this.minX + this.maxX) / 2;
        const cz = (this.minZ + this.maxZ) / 2;
        this.bolt.position.set(cx + Math.cos(a) * 1500, this.roadMinY - 40, cz + Math.sin(a) * 1500);
        this.bolt.rotation.y = this.rand() * Math.PI;
      }
    }
    if (this.flash > 0) this.flash -= dt;
    const on = this.flash > 0 && (this.flash > 0.25 || this.flash < 0.12);
    this.hemi.intensity = this.baseHemi + (on ? 1.6 : 0);
    if (this.domeMat?.emissiveTexture) this.domeMat.emissiveTexture.level = on ? 2.2 : 1;
    this.bolt?.setEnabled(on);
  }

  dispose(): void {
    this.disposeReflections();
    for (const m of this.meshes) m.dispose(false, false);
    for (const m of this.mats) m.dispose(true, true);
    for (const t of this.textures) t.dispose();
    this.meshes.length = 0;
    this.dolphins = [];
  }
}
